import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PlanTypeV4 } from "@markit/common.types";
import { hasSubscription } from "@markit/common.utils";
import { Icon } from "@iconify/react";
import { CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { MarkitAPI } from "@markit/common.api";
import { API } from "../../../../API";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { notificationActions } from "../../../../redux/slices/notificationSlice";
import { Colors } from "../../../../utils/colors";
import LabelButton from "../../../Buttons/LabelButton";

type InterpretResult = MarkitAPI.Text.SelectRecipientsAi.Interpret["response"];
type Categories = NonNullable<InterpretResult["categories"]>;

// Contact ids come back in pages of about 5,000: this is far above any
// account's audience, and only stops a server that never ends a page chain.
const MAX_ID_PAGES = 40;

type SelectRecipientsAiBarProps = {
  /** The people selected now. A request that only leaves people out narrows these. */
  allSelectedPeople: string[];
  /** The subscribed followers Select All Followers would select. */
  visibleFollowerIds: string[];
  /**
   * Whether `visibleFollowerIds` is every subscribed follower. It is filled in
   * page by page, so until then "all followers" is read from the server.
   */
  allFollowersLoaded: boolean;
  /** Replaces the whole selection. */
  onSelect: (ids: string[]) => void;
  /**
   * The event (or product) of an automated text whose recipients are
   * categories. With it the bar answers in categories, which stay live until
   * the text sends, and Apply calls `onApplyCategories`.
   */
  eventId?: string;
  /** Replaces the categories on the text. */
  onApplyCategories?: (categories: Categories) => void;
};

type Status = "idle" | "loading" | "result" | "applying" | "error";

// The server's own words for a refusal (the hourly limit, a plan, bad input),
// when it sent one, so the card can say what to do instead of a generic error.
const serverMessage = (error: any): string | null => {
  const message = error?.response?.data?.error;
  return typeof message === "string" && message ? message : null;
};

// A missing plan, as opposed to a failure or a failed payment, opens the plans modal.
const isPlanRefusal = (error: any): boolean =>
  error?.response?.status === 403 && error?.response?.data?.code === "no_plan";

const notTextableText = (
  notTextable: NonNullable<InterpretResult["notTextable"]>
): string =>
  [
    [notTextable.unsubscribed, "unsubscribed"],
    [notTextable.removed, "removed"],
    [notTextable.invalidNumber, "invalid number"],
    [notTextable.nonUsNumber, "non-US"],
  ]
    .filter(([count]) => (count as number) > 0)
    .map(([count, reason]) => `${count} ${reason}`)
    .join(" · ");

const SelectRecipientsAiBar = (props: SelectRecipientsAiBarProps) => {
  const {
    allSelectedPeople,
    visibleFollowerIds,
    allFollowersLoaded,
    onSelect,
    eventId,
    onApplyCategories,
  } = props;
  const dispatch = useDispatch();
  const { accountData } = useSelector(getAccountState).account;
  const [prompt, setPrompt] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<InterpretResult | null>(null);
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  // One idea made from a few small reads and no model, so it shows at once.
  // Undefined while it loads, null when there is none (then none is shown).
  const [quick, setQuick] = useState<string | null | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  // Bumped by Clear, so an answer that arrives after it is dropped.
  const runRef = useRef(0);
  // The selection as it is now, for an Apply that finishes after it changed.
  const selectedRef = useRef(allSelectedPeople);
  selectedRef.current = allSelectedPeople;
  // The event the suggestions were asked for: they are asked once, the first
  // time the bar is focused, since each ask runs the model.
  const suggestionsFor = useRef<string | null>(null);

  const hasPlan = useMemo(() => hasSubscription(accountData), [accountData]);

  const openPlans = useCallback(() => {
    dispatch(notificationActions.setMarkitPlusModal(PlanTypeV4.ESSENTIAL.name));
  }, [dispatch]);

  const submit = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || status === "loading" || status === "applying") {
        return;
      }
      if (!hasPlan) {
        openPlans();
        return;
      }
      const run = ++runRef.current;
      setStatus("loading");
      setErrorMessage(null);
      setResult(null);
      try {
        const response = await API.text.selectRecipientsAiInterpret({
          prompt: trimmed,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...(eventId
            ? { eventId }
            : {
                selectedIds:
                  allSelectedPeople.length > 0 ? allSelectedPeople : undefined,
              }),
        });
        if (run !== runRef.current) {
          return;
        }
        setResult(response);
        setStatus("result");
      } catch (error: any) {
        if (run !== runRef.current) {
          return;
        }
        setErrorMessage(serverMessage(error));
        setStatus("error");
        if (isPlanRefusal(error)) {
          openPlans();
        }
      }
    },
    [allSelectedPeople, eventId, hasPlan, openPlans, status]
  );

  const apply = useCallback(async () => {
    if (result?.categories && onApplyCategories) {
      if (!result.textable) {
        return;
      }
      onApplyCategories(result.categories);
      setPrompt("");
      setResult(null);
      setStatus("idle");
      return;
    }
    if (
      !result ||
      (!result.filter && result.contactIds.length === 0) ||
      !result.textable
    ) {
      return;
    }
    const request = {
      filter: result.filter ?? undefined,
      contactIds: result.contactIds,
      selectedIds: allSelectedPeople.length > 0 ? allSelectedPeople : undefined,
    };
    const run = ++runRef.current;
    setStatus("applying");
    setErrorMessage(null);
    try {
      let ids: string[] = [];
      const first = (await API.text.selectRecipientsAiResolve(request))
        .selection;
      if (first.type === "all_followers" && allFollowersLoaded) {
        ids = visibleFollowerIds;
      } else {
        // A single list is small, and the server's list of who can be texted
        // is the one the count above was taken from, so ask for its ids.
        let page = (
          first.type === "ids"
            ? { selection: first }
            : await API.text.selectRecipientsAiResolve({
                ...request,
                includeIds: true,
              })
        ).selection;
        for (let pages = 0; pages < MAX_ID_PAGES; pages++) {
          if (page.type !== "ids") {
            break;
          }
          ids = ids.concat(page.ids);
          if (!page.nextCursor) {
            break;
          }
          page = (
            await API.text.selectRecipientsAiResolve({
              ...request,
              includeIds: true,
              cursor: page.nextCursor,
            })
          ).selection;
        }
      }
      if (run !== runRef.current) {
        return;
      }
      // New people are added to the selection, unless the sentence only left
      // some out of it.
      onSelect(
        result.narrowsSelection
          ? ids
          : Array.from(new Set([...selectedRef.current, ...ids]))
      );
      setPrompt("");
      setResult(null);
      setStatus("idle");
    } catch (error: any) {
      if (run !== runRef.current) {
        return;
      }
      setErrorMessage(serverMessage(error));
      setStatus("error");
    }
  }, [
    allFollowersLoaded,
    allSelectedPeople,
    onApplyCategories,
    onSelect,
    result,
    visibleFollowerIds,
  ]);

  // Ideas for what to type, asked for the first time the bar is focused. A
  // failure or an empty answer just means there is nothing to show.
  const loadSuggestions = useCallback(async () => {
    const key = eventId ?? "";
    if (suggestionsFor.current === key) {
      return;
    }
    suggestionsFor.current = key;
    setSuggestions([]);
    setQuick(undefined);
    setSuggestionsLoading(true);
    try {
      // The quick idea first: a few small reads, no model. The ideas the model
      // writes are asked for after it, so they can be about something else.
      const response = await API.text.selectRecipientsAiSuggest({
        eventId,
        quick: true,
      });
      if (suggestionsFor.current === key) {
        setQuick(response.suggestions[0] ?? null);
      }
    } catch (error) {
      if (suggestionsFor.current === key) {
        setQuick(null);
      }
    }
    try {
      const response = await API.text.selectRecipientsAiSuggest({ eventId });
      if (suggestionsFor.current === key) {
        setSuggestions(response.suggestions);
      }
    } catch (error) {
      // Ideas are a nicety, so no card, but say why in the console.
      console.warn("Could not load recipient suggestions", error);
      setSuggestions([]);
    } finally {
      if (suggestionsFor.current === key) {
        setSuggestionsLoading(false);
      }
    }
  }, [eventId]);

  // A different event's text has different ideas.
  useEffect(() => {
    suggestionsFor.current = null;
    setSuggestions([]);
  }, [eventId]);

  const styles = {
    card: {
      position: "relative",
      border: `1px solid ${Colors.GRAY4}`,
      borderRadius: 14,
      padding: 14,
      gap: 10,
      overflow: "hidden",
      backgroundColor: Colors.WHITE,
    } as CSSProperties,
    body: { gap: 4 } as CSSProperties,
    // A highlight that sweeps across the box while the answer is made.
    shimmer: {
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      backgroundImage: `linear-gradient(90deg, transparent 0%, ${Colors.PINK2}2E 50%, transparent 100%)`,
      backgroundSize: "200% 100%",
      animation: "recipientsAiShimmer 1.3s linear infinite",
    } as CSSProperties,
    skeleton: {
      height: 28,
      width: 120,
      borderRadius: 100,
      backgroundColor: `${Colors.PINK2}1A`,
      border: `0.5px solid ${Colors.PINK2}33`,
      animation: "recipientsAiPulse 1.2s ease-in-out infinite",
    } as CSSProperties,
  };

  const busy = status === "loading" || status === "applying";
  const typing = prompt.trim().length > 0;
  const locked = !hasPlan;
  const recapShown =
    result !== null && (status === "result" || status === "applying");
  const suggestionsShown = focused && !locked && status === "idle" && !typing;
  // The quick idea is there at once, and the others fill in when they arrive.
  const ideas = quick
    ? [quick, ...suggestions.filter((idea) => idea !== quick)]
    : [];

  const countText = (count: number) =>
    `${count} ${count === 1 ? "recipient matches" : "recipients match"}`;

  const changeText = (change: { added: number; removed: number }) =>
    change.removed > 0
      ? `${change.removed} will be removed from your selection`
      : `${change.added} will be added to your selection`;

  // Someone in the result can be texted, so the card applies it.
  const applicable = !!result?.textable;
  const canApply = status === "result" && applicable;

  return (
    <div className="ColumnNormal" style={{ gap: 14, paddingBottom: 14 }}>
      <style>
        {
          "@keyframes recipientsAiShimmer { 0% { background-position: 100% 0; } 100% { background-position: -100% 0; } } @keyframes recipientsAiPulse { 0%, 100% { opacity: 0.45; } 50% { opacity: 1; } }"
        }
      </style>
      <div
        className="ColumnNormal"
        style={{
          position: "relative",
          overflow: "hidden",
          padding: "0 10px",
          borderRadius: 12,
          border: `1px solid ${
            focused && !locked ? Colors.PINK2 : Colors.GRAY4
          }`,
          boxShadow:
            focused && !locked ? `0 0 0 3px ${Colors.PINK2}22` : "none",
        }}
      >
        {status === "loading" ? (
          <div aria-hidden="true" style={styles.shimmer} />
        ) : null}
        <div className="AlignedRow" style={{ gap: 8, minHeight: 42 }}>
          <Icon
            icon={locked ? "ph:lock-simple" : "ph:sparkle-fill"}
            width={20}
            style={{ color: locked ? Colors.GRAY1 : Colors.PINK2 }}
          />
          <input
            ref={inputRef}
            className="bodyRegular"
            value={prompt}
            readOnly={busy}
            placeholder="Describe who you want to text..."
            aria-label="Describe who you want to text"
            onChange={(e) => {
              setPrompt(e.target.value.slice(0, 500));
              // The result was for the old words: drop it, and the arrow asks again.
              if (status === "result" || status === "error") {
                runRef.current += 1;
                setResult(null);
                setStatus("idle");
              }
            }}
            onFocus={() => {
              setFocused(true);
              if (locked) {
                openPlans();
              } else if (!prompt) {
                loadSuggestions();
              }
            }}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                submit(prompt);
              }
            }}
            style={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              background: "transparent",
              fontFamily: "inherit",
              padding: "10px 0",
            }}
          />
          {locked ? (
            <LabelButton
              color={Colors.WHITE}
              backgroundColor={Colors.PINK2}
              borderColor={Colors.PINK2}
              text="Get Markit+"
              onPress={openPlans}
            />
          ) : status === "loading" ? (
            <CircularProgress size={16} style={{ color: Colors.PINK2 }} />
          ) : typing ? (
            <div
              className="AlignedRow"
              role="button"
              tabIndex={0}
              aria-label="Find recipients"
              onClick={() => submit(prompt)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  submit(prompt);
                }
              }}
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                flexShrink: 0,
                cursor: "pointer",
                justifyContent: "center",
                backgroundColor: Colors.PINK2,
              }}
            >
              <Icon
                icon="ph:arrow-up-bold"
                width={14}
                style={{ color: Colors.WHITE }}
              />
            </div>
          ) : null}
        </div>
        {suggestionsShown ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              paddingBottom: 10,
            }}
            // Keep the focus in the input, so choosing an idea doesn't close this.
            onMouseDown={(e) => e.preventDefault()}
          >
            {ideas.map((idea) => (
              <LabelButton
                key={idea}
                color={Colors.PINK2}
                backgroundColor={`${Colors.PINK2}1A`}
                borderColor={Colors.PINK2}
                text={idea}
                containerStyles={{ maxWidth: "100%" }}
                onPress={() => {
                  setPrompt(idea);
                  inputRef.current?.focus();
                }}
              />
            ))}
            {suggestionsLoading
              ? Array.from({ length: quick === undefined ? 3 : 2 }).map((_, index) => (
                  <div key={index} aria-hidden="true" style={styles.skeleton} />
                ))
              : null}
          </div>
        ) : null}
      </div>
      {status === "error" ? (
        <div className="ColumnNormal" style={styles.card} role="alert">
          <div className="ColumnNormal" style={styles.body}>
            {errorMessage ? (
              <span className="bodyRegular">{errorMessage}</span>
            ) : (
              <span className="bodyMedium" style={{ color: Colors.RED3 }}>
                An Error Occurred
              </span>
            )}
          </div>
        </div>
      ) : recapShown && result ? (
        result.status === "needs_clarification" ? (
          <div className="ColumnNormal" style={styles.card} role="status">
            <div className="ColumnNormal" style={styles.body}>
              <span className="bodyBold">{result.summary}</span>
              {result.options.length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    marginTop: 4,
                  }}
                >
                  {result.options.map((option) => (
                    <LabelButton
                      key={option}
                      color={Colors.PINK2}
                      backgroundColor={`${Colors.PINK2}1A`}
                      borderColor={Colors.PINK2}
                      text={option}
                      onPress={() =>
                        submit(
                          `${prompt.slice(0, 500 - option.length - 3)} (${option})`
                        )
                      }
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : result.status === "unsupported" ? (
          <div className="ColumnNormal" style={styles.card}>
            <div className="ColumnNormal" style={styles.body}>
              <span className="bodyRegular">{result.summary}</span>
            </div>
          </div>
        ) : (
          // The whole card applies the selection.
          <div
            className="ColumnNormal"
            style={{
              ...styles.card,
              border: `1px solid ${applicable ? Colors.PINK2 : Colors.GRAY4}`,
              cursor: canApply ? "pointer" : "default",
            }}
            role={canApply ? "button" : "status"}
            tabIndex={canApply ? 0 : undefined}
            onClick={canApply ? apply : undefined}
            onKeyDown={(e) => {
              if (canApply && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                apply();
              }
            }}
          >
            <div className="ColumnNormal" style={styles.body}>
              <span className="bodyRegular">{result.summary}</span>
              <span className="bodyMedium">{countText(result.count ?? 0)}</span>
              {result.notTextable &&
              result.count !== result.textable &&
              notTextableText(result.notTextable) ? (
                <span className="smallBodySubtext">
                  {`${result.textable} can be texted · ${notTextableText(
                    result.notTextable
                  )}`}
                </span>
              ) : null}
              {result.change ? (
                <span className="smallBodySubtext">
                  {changeText(result.change)}
                </span>
              ) : null}
              {result.categories ? (
                <span className="smallBodySubtext">
                  Replaces the categories on this text. They stay live until it
                  sends, so people who match later are included.
                </span>
              ) : null}
            </div>
            {applicable ? (
              <div
                className="AlignedRow"
                style={{ gap: 4, color: Colors.PINK2 }}
              >
                <span className="bodySmallMedium">Apply</span>
                <Icon
                  icon="ion:arrow-forward"
                  width={12}
                  style={{ transform: "rotateY(0deg) rotate(-45deg)" }}
                />
              </div>
            ) : null}
          </div>
        )
      ) : null}
    </div>
  );
};

export default SelectRecipientsAiBar;
